import React, { useState, useEffect } from 'react';
import { Accordion, Button } from 'react-bootstrap';
import {
    EAC_PHONE_TYPE, EAC_RELATIONSHIPS,
    POSITIONS,
    US_STATE,
    VALIDATIONS,
} from '../../../../constants';
import { optionValues, radioOptionValues } from '../../../../utils/utils';
import SharedForm from '../../../../components/ui/Form';
import Icon from '../../../../components/ui/Icon';
import CareGiverRepeater from './CareGiverRepeater';
import '../../../../../src/App.scss';


const OutreachContacts = ({ proxy, emergencyContact, newCareGivers, careGivers, onInputChange, onAccordionChange, onFormValidate }) => {

    const [addCareGiverForm, setAddCareGiverForm] = useState(false);
    const [newCareGiverCount, setNewCareGiverCount] = useState(1);
    const [activeKey, setActiveKey] = useState(null);

    const emergencyContactArr = [
        {
            key: 'emergencyFirstName',
            labelName: 'First Name',
            placeholderName: 'First Name',
            type: 'text',
            valid: VALIDATIONS.String.isName,
            maxLength: 35,
            required: false
        },
        {
            key: 'emergencyLastName',
            labelName: 'Last Name',
            placeholderName: 'Last Name',
            type: 'text',
            valid: VALIDATIONS.String.isName,
            maxLength: 35,
            required: false
        },
        {
            key: 'phoneEmergency',
            labelName: 'Phone Number',
            placeholderName: 'Phone Number',
            type: 'tel',
            maxLength: 12,
            required: false,
            valid: VALIDATIONS.Number.isPhone
        },
        {
            key: 'phoneTypeEmergency',
            labelName: '',
            type: 'radio',
            required: false,
            optionValues: radioOptionValues(EAC_PHONE_TYPE),
        },
        {
            key: 'relationshipEmergency',
            labelName: 'Relationship',
            type: 'options',
            optionValues: optionValues(EAC_RELATIONSHIPS, true, 'Select Relationship'),
            required: false,
        }
    ];

    const proxyArr = [
        {
            key: 'proxyFirstName',
            labelName: ' Proxy First Name',
            placeholderName: 'Proxy First Name',
            type: 'text',
            valid: VALIDATIONS.String.isName,
            maxLength: 35,
            required: false,
        },
        {
            key: 'proxyLastName',
            labelName: 'Proxy Last Name',
            placeholderName: 'Proxy Last Name',
            type: 'text',
            valid: VALIDATIONS.String.isName,
            maxLength: 35,
            required: false,
        },
        {
            key: 'proxyPhone',
            labelName: 'Proxy Phone',
            placeholderName: 'Proxy Phone',
            type: 'tel',
            maxLength: 12,
            required: false,
            valid: VALIDATIONS.Number.isPhone,
        },
        {
            key: 'proxyAddress1',
            labelName: 'Proxy Address',
            placeholderName: 'Proxy Address',
            type: 'text',
            required: false,
            maxLength: 35,
        },
        {
            key: 'proxyAddress2',
            labelName: 'Proxy Address Line 2',
            placeholderName: 'Proxy Address Line 2',
            type: 'text',
            required: false,
            maxLength: 35,
        },
        {
            key: 'proxyCity',
            labelName: 'Proxy City',
            placeholderName: 'Proxy City',
            type: 'text',
            required: false,
            maxLength: 50,
        },
        {
            key: 'proxyState',
            labelName: 'Proxy State',
            placeholderName: 'Proxy State',
            type: 'options',
            optionValues: optionValues(US_STATE, true, 'Proxy State'),
            minLength: 2,
            maxLength: 2,
            required: false,
        },
        {
            key: 'proxyPostalCode',
            labelName: 'Proxy Zip',
            placeholderName: 'Proxy Zip',
            type: 'text',
            minLength: 5,
            maxLength: 9,
            valid: VALIDATIONS.Number.isZipCode,
            required: false,
        },
        {
            key: 'proxyEmail',
            labelName: 'Proxy Email',
            placeholderName: 'Proxy Email',
            type: 'email',
            maxLength: 255,
            required: false
        },
    ];

    const handleSelect = (key) => {
        setActiveKey(key);
        onAccordionChange(key);
    }

    const handleNewCareGiver = () => {
        // add new care giver fields
        setNewCareGiverCount(prevState => prevState + 1);
        const key = `careGiver_new_${newCareGiverCount + 1}`;
        setActiveKey(key);
        setAddCareGiverForm(true);
        onAccordionChange(key);
    }

    useEffect(() => {
        if (careGivers?.length > 0 && newCareGivers?.length === 0) {
            setNewCareGiverCount(0);
        }
    }, [careGivers]);

    return (
        <div className="accordion-lined px-xl-2">
            <Accordion activeKey={activeKey} onSelect={handleSelect}>
                {(addCareGiverForm || careGivers?.length === 0) &&
                    <>
                        {Array.from({ length: newCareGiverCount }, (_, index) => (
                            <CareGiverRepeater
                                key={`new_${index + 1}`}
                                eventKey={`careGiver_new_${index + 1}`}
                                fieldPrefix={`new_${index + 1}`}
                                title={`Add Caregiver ${index > 0 ? index + 1 : ''}`}
                                onInputChange={onInputChange}
                                onFormValidate={onFormValidate}
                            />
                        ))}
                    </>
                }
                {careGivers?.length > 0 && careGivers.map(cg => {
                    const keysArr = Object.keys(cg);
                    const firstNameKey = keysArr.find(key => key.includes('firstName'));
                    const lastNameKey = keysArr.find(key => key.includes('lastName'));
                    const idKey = keysArr.find(key => key.includes('_id'));
                    return (
                        <CareGiverRepeater
                            key={cg[idKey]}
                            modelObj={cg}
                            eventKey={`careGiver_${cg[idKey]}`}
                            fieldPrefix={cg[idKey]}
                            title={`Caregiver: ${cg[firstNameKey]} ${cg[lastNameKey]}`}
                            onInputChange={onInputChange}
                            onFormValidate={onFormValidate}
                        />
                    )
                })
                }
                <Accordion.Item eventKey="emergencyContact">
                    <Accordion.Header>Emergency Contact</Accordion.Header>
                    <Accordion.Body>
                        <SharedForm
                            cols={12}
                            array={emergencyContactArr}
                            modelObj={emergencyContact}
                            onInputChanged={onInputChange}
                            onFormValidate={onFormValidate}
                            actionBarPosition={POSITIONS.NONE}
                        />
                    </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey="proxy">
                    <Accordion.Header>Add Proxy</Accordion.Header>
                    <Accordion.Body>
                        <SharedForm
                            cols={12}
                            array={proxyArr}
                            modelObj={proxy}
                            onInputChanged={onInputChange}
                            onFormValidate={onFormValidate}
                            actionBarPosition={POSITIONS.NONE}
                        />
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
            <div className="mt-3">
                <Button
                    variant="link"
                    className="text-secondary text-decoration-none d-flex align-items-center"
                    onClick={handleNewCareGiver}>
                    <Icon
                        icon="new"
                        size={10}
                        className="me-2"
                    />
                    Add Caregiver
                </Button>
            </div>
        </div>
    );
};

export default OutreachContacts;
